import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import history from "@/routes/history";

import { useGetUserTreeData } from "./userTreeData.hooks";
import {
  Member,
  ManagementType,
  UserManagementModalProps,
  RemoveEntityModalProps,
} from "../types/commonTypes";

interface Sector {
  id: string;
  zip: string;
}

interface CustomRole {
  value: string;
  label: string;
}

interface TeamRole {
  role: CustomRole;
}

interface CustomTeam {
  id: string;
  label: string;
  members: Member[];
  sectors: Sector[];
  teamRole: TeamRole;
}

interface GetTeamTreeDataProps {
  agencyId: string;
  teams: CustomTeam[];
  handleUserManagementModal?: (props: UserManagementModalProps) => void;
  handleRemoveEntityModal?: (props: RemoveEntityModalProps) => void;
}

export const useGetTeamTreeData = () => {
  const { getUserTreeData } = useGetUserTreeData();

  const { canCreateUser, canViewTeam, canUpdateTeam, canDeleteTeam } =
    useGetUserType();

  const getTeamTreeData = ({
    agencyId,
    teams,
    handleUserManagementModal,
    handleRemoveEntityModal,
  }: GetTeamTreeDataProps) => {
    if (!agencyId || !teams) return [];

    return teams.map((team) => ({
      id: team.id,
      parentId: agencyId,
      name: team.label,
      type: ManagementType.TEAM,
      sector: team.sectors.map((s) => s.zip),
      role: team.teamRole.role.label,
      actions: [
        ...(canCreateUser
          ? [
              {
                text: (
                  <span>
                    Ajouter <span className="font-bold">un utilisateur</span>{" "}
                    dans cette équipe
                  </span>
                ),
                icon: <i className="pi pi-plus text-[15px] text-extrabold" />,
                onClick: () => {
                  handleUserManagementModal?.({
                    actionType: "add",
                    teamId: team.id,
                    teamLabel: team.label,
                  });
                },
              },
            ]
          : []),
        ...(canUpdateTeam
          ? [
              {
                text: "Editer cette équipe",
                icon: <i className="pi pi-pencil text-[14px]" />,
                onClick: () => {
                  history.push(`/management/team/${team?.id}`);
                },
              },
            ]
          : []),
        ...(canViewTeam
          ? [
              {
                text: "Consulter cette équipe",
                icon: <i className="pi pi-eye text-[16.6px]" />,
                onClick: () => {
                  history.push(`/management/team/${team?.id}`, {
                    readOnly: true,
                  });
                },
              },
            ]
          : []),
        ...(canDeleteTeam
          ? [
              {
                text: "Supprimer cette équipe",
                icon: (
                  <i className="pi pi-trash text-[14px] text-red-FF0000 font-semibold" />
                ),
                onClick: () => {
                  handleRemoveEntityModal?.({
                    id: team?.id,
                    userType: ManagementType.TEAM,
                    actionType: "delete",
                    label: team?.label,
                  });
                },
              },
            ]
          : []),
      ],
      children: getUserTreeData({
        teamId: team.id,
        members: team.members,
        handleUserManagementModal,
        handleRemoveEntityModal,
      }),
    }));
  };

  return { getTeamTreeData };
};
